/**
 * One navigation blocker per editor, shared by everything nested in it.
 *
 * react-router consults only the most recently registered `useBlocker`, so a
 * module editor's own guard mounted inside the programme's replaced it, and
 * the programme's unsaved work left without a prompt. Nested editors register
 * a guard here instead, and the host's single blocker asks every one.
 *
 * @module router/departure-guard
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { type BlockerFunction, useBlocker } from "react-router";

/** Sends what a guard holds for; true once nothing is left unsaved. */
type Settle = () => Promise<boolean>;

interface Guard {
  held: BlockerFunction;
  settle?: Settle;
}

type Register = (guard: Guard) => () => void;

const DepartureGuardContext = createContext<Register | null>(null);

/**
 * The host's blocker: its own guard, or any nested one.
 *
 * A held move waits while each guard that held it settles, and goes ahead
 * once all have. It asks the reader only when one cannot, or has no settle.
 *
 * @param own - The host's own guard.
 * @returns The blocker, whether the host's own guard is what held the last
 *   navigation, whether the held move is still waiting on a save, and the
 *   register its nested guards reach through {@link DepartureGuardScope}.
 */
export function useDepartureGuardHost(own: Guard): {
  blocker: ReturnType<typeof useBlocker>;
  heldByOwn: boolean;
  settling: boolean;
  register: Register;
} {
  const nested = useRef(new Set<Guard>());
  const held = useRef<Guard[]>([]);
  // Which guard held decides what the prompt names: the host's own status
  // says nothing about a move its own guard let through.
  const [heldByOwn, setHeldByOwn] = useState(false);
  const blocker = useBlocker((args) => {
    held.current = [own, ...nested.current].filter((guard) => guard.held(args));
    setHeldByOwn(held.current[0] === own);
    return held.current.length > 0;
  });
  const latestBlocker = useRef(blocker);
  useLayoutEffect(() => {
    latestBlocker.current = blocker;
  });

  // Keyed by the held move, so a later one starts settling afresh.
  const [unsettled, setUnsettled] = useState<string | null>(null);
  const heldKey = blocker.state === "blocked" ? blocker.location.key : null;
  useEffect(() => {
    if (heldKey === null) {
      return undefined;
    }
    let current = true;
    Promise.all(
      held.current.map((guard) => guard.settle?.().catch(() => false) ?? Promise.resolve(false)),
    )
      .then((settled) => {
        if (!current) {
          return;
        }
        if (settled.every(Boolean)) {
          latestBlocker.current.proceed?.();
        } else {
          setUnsettled(heldKey);
        }
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [heldKey]);

  const register = useCallback<Register>((guard) => {
    nested.current.add(guard);
    return () => {
      nested.current.delete(guard);
    };
  }, []);
  return { blocker, heldByOwn, settling: heldKey !== null && unsettled !== heldKey, register };
}

/**
 * Asks the browser to confirm closing the tab while `holding` says work would
 * be lost. Read at unload, since an edit in a debounce changes it without a
 * render.
 */
export function useUnloadGuard(holding: () => boolean): void {
  const latest = useRef(holding);
  useLayoutEffect(() => {
    latest.current = holding;
  });
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent): void => {
      if (latest.current()) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);
}

export function DepartureGuardScope({
  register,
  children,
}: {
  register: Register;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <DepartureGuardContext.Provider value={register}>{children}</DepartureGuardContext.Provider>
  );
}

/**
 * Registers a guard with the enclosing host. Outside one it guards nothing:
 * every production editor is nested in the programme layout.
 *
 * @param guard - Whether a navigation should be held.
 * @param settle - Sends what the guard holds for. Without one the reader is
 *   always asked.
 */
export function useDepartureGuard(guard: BlockerFunction, settle?: Settle): void {
  const register = useContext(DepartureGuardContext);
  const latest = useRef({ guard, settle });
  useLayoutEffect(() => {
    latest.current = { guard, settle };
  });
  useEffect(
    () =>
      register?.({
        held: (args) => latest.current.guard(args),
        settle: () => latest.current.settle?.() ?? Promise.resolve(false),
      }),
    [register],
  );
}
